import {Button, Stack} from "@mui/material";
import {useEffect, useMemo, useRef, useState} from "react";
import useApiRequest from "../../hooks/useApiRequest";
import SearchAutocomplete from "../ui/SearchAutocomplete";

// Límite máximo admitido por los esquemas de query del backend
const MAX_LIMIT = 100;

const normalizeArray = (value) => {
  if (Array.isArray(value?.data)) return value.data;
  if (Array.isArray(value?.items)) return value.items;
  if (Array.isArray(value?.results)) return value.results;
  if (Array.isArray(value)) return value;
  return [];
};

const TenantUserRolesAddForm = ({onAccept, onCancel, tenantId = null, productId = null}) => {
  const [selectedUserId, setSelectedUserId] = useState(null);
  const [selectedRoleId, setSelectedRoleId] = useState(null);
  const [userValue, setUserValue] = useState(null);
  const [roleValue, setRoleValue] = useState(null);
  const [userInput, setUserInput] = useState("");
  const [roleInput, setRoleInput] = useState("");

  const [assignableRoles, setAssignableRoles] = useState([]);
  const [loadingRoles, setLoadingRoles] = useState(false);

  const loadTenantProducts = !!tenantId && !productId;

  const {
    data: dataUsers,
    loading: loadingUsers,
    list: listUsers
  } = useApiRequest({baseUrl: "/users", resource: "users"});
  const {list: listRoles} = useApiRequest({baseUrl: "/roles", resource: "roles"});
  const {data: dataProducts, list: listTenantProducts} = useApiRequest({
    baseUrl: `/tenants/${tenantId}/products`,
    resource: "",
    field: ""
  });

  const listRolesRef = useRef(listRoles);
  useEffect(() => {
    listRolesRef.current = listRoles;
  });

  useEffect(() => {
    listUsers();
    if (loadTenantProducts) listTenantProducts({page: 0, limit: MAX_LIMIT});
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [loadTenantProducts]);

  const productIdsKey = productId
    ? productId
    : normalizeArray(dataProducts)
        .map((p) => p.product_id || p.id)
        .filter(Boolean)
        .join(",");

  useEffect(() => {
    const ids = productIdsKey ? productIdsKey.split(",") : [];
    if (!ids.length) {
      setAssignableRoles([]);
      return;
    }

    let cancelled = false;
    setLoadingRoles(true);
    Promise.all(ids.map((pid) => listRolesRef.current({product_id: pid, page: 0, limit: MAX_LIMIT})))
      .then((results) => {
        if (cancelled) return;
        const byId = new Map();
        results.forEach((res) => {
          if (!res?.success) return;
          normalizeArray(res.data).forEach((r) => r?.id && byId.set(r.id, r));
        });
        setAssignableRoles([...byId.values()]);
      })
      .finally(() => {
        if (!cancelled) setLoadingRoles(false);
      });

    return () => {
      cancelled = true;
    };
  }, [productIdsKey]);

  const usersOptions = useMemo(
    () => normalizeArray(dataUsers).map((u) => ({id: u.id, label: u.email || u.id})),
    [dataUsers]
  );

  const rolesOptions = useMemo(() => {
    const search = roleInput.trim().toLowerCase();
    const options = assignableRoles.map((r) => ({
      id: r.id,
      label: [r.product_name, r.name || r.id].filter(Boolean).join(" — ")
    }));
    return search ? options.filter((o) => o.label.toLowerCase().includes(search)) : options;
  }, [assignableRoles, roleInput]);

  const handleAccept = () => {
    if (!selectedUserId || !selectedRoleId) return;
    onAccept(selectedUserId, selectedRoleId);
  };

  return (
    <Stack direction="row" spacing={2} alignItems="center">
      <SearchAutocomplete
        label="Usuario"
        options={usersOptions}
        loading={loadingUsers}
        value={userValue}
        inputValue={userInput}
        onInputChange={(_, v) => setUserInput(v)}
        onChange={(_, value) => {
          setUserValue(value);
          setSelectedUserId(value?.id || null);
        }}
        onSearch={() => listUsers(userInput ? {email: userInput} : undefined)}
        width={300}
        size="normal"
      />
      <SearchAutocomplete
        label="Rol"
        options={rolesOptions}
        loading={loadingRoles}
        value={roleValue}
        inputValue={roleInput}
        onInputChange={(_, v) => setRoleInput(v)}
        onChange={(_, value) => {
          setRoleValue(value);
          setSelectedRoleId(value?.id || null);
        }}
        onSearch={() => {}}
        width={300}
        size="normal"
      />
      <Button variant="contained" disabled={!selectedUserId || !selectedRoleId} onClick={handleAccept}>
        Aceptar
      </Button>
    </Stack>
  );
};

export default TenantUserRolesAddForm;
